import { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import { differenceInMonths, formatDistanceToNow } from "date-fns";
import { Container, Grid, Icon, Loader } from "semantic-ui-react";
import { useTheme } from "styled-components";

import { Input } from "simplydo/react/component-library";

import api from "api";
import util from "utils/utils";

import NotFound from "components/NotFound";
import TagModalChooser from "components/lib/Choosers/Tags/TagModalChooser";
import RowComponent from "components/lib/RowComponent";
import { SideNav } from "components/lib/SideNav";
import { Banner } from "components/lib/UI";

const Tags = ({ user }) => {
  const navigate = useNavigate();

  const params = useParams();
  const tagIds = params.ids?.split(",") || [];
  const allTags = user.ownerOrganisation.tagList;
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [tagData, setTagData] = useState({ tags: [] });
  const currentTags = tagData.tags;
  const [searchValue, setSearchValue] = useState("");

  const { t } = useTranslation();

  useEffect(() => {
    const _tagIds = params.ids?.split(",") || [];
    setLoading(true);
    if (_tagIds.length > 0) {
      api.tags.full(_tagIds, (newTagData) => {
        setTagData(newTagData);
        setLoading(false);
      });
    } else {
      setTagData({ tags: [], collections: {} });
      setLoading(false);
    }
  }, [params.ids]);

  if (!loading && !tagData) {
    return <NotFound />;
  }

  const { collections = {} } = tagData || {};
  const { ideas = [], challenges = [], users = [] } = collections;
  const combinedLength = Object.values(collections).reduce(
    (p, c) => p + c.length,
    0,
  );

  return (
    <div>
      <Helmet title={"Tags"} />

      <Banner marginless>
        <Container
          style={{ paddingTop: 10, height: theme.sizes.isMobile && 100 }}
        >
          <Grid stackable>
            <Grid.Column computer={8}>
              <h1 style={{ color: "#3b3b3b", marginBottom: 2 }}>
                Tag explorer
              </h1>
              {tagIds.length && !theme.sizes.isMobile ?
                <p style={{ marginBottom: theme.sizes.isMobile && "-20px" }}>
                  Results for{" "}
                  {tagData?.tags
                    ?.slice(0, 3)
                    .map((t) => `"${t.value}"`)
                    .join(", ")}{" "}
                  {tagIds.length > 3 ?
                    `and ${tagIds.length - 3} other(s).`
                  : ""}
                </p>
              : <p style={{ marginBottom: theme.sizes.isMobile && "-20px" }}>
                  Find all tagged documents
                </p>
              }
            </Grid.Column>
          </Grid>
        </Container>
      </Banner>
      <SideNav
        nav={
          <div style={{ marginTop: 10, padding: 15 }}>
            <div style={{ marginBottom: 25 }}>
              <h4>Search</h4>
              <Input
                icon="search"
                iconPosition="left"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search..."
              />
            </div>
            <TagModalChooser
              tags={currentTags}
              availableTags={allTags}
              addTag={(t) => {
                const updatedTags = [...tagIds, t._id];
                navigate(`/tags/${updatedTags.join(",")}`);
              }}
              removeTag={(t) => {
                const index = tagIds.findIndex((ti) => ti === t._id);
                if (index !== -1) {
                  const updatedTags = [...tagIds];
                  updatedTags.splice(index, 1);
                  navigate(`/tags/${updatedTags.join(",")}`);
                }
              }}
            />
          </div>
        }
        main={
          <>
            {loading && <Loader active inline="centered" />}
            {!loading && !tagIds.length ?
              <>
                <h1>No tags selected</h1>
                <p>
                  Use the sidebar filter to the right to search through tagged
                  items
                </p>
              </>
            : null}
            {!loading && tagIds.length && combinedLength === 0 ?
              <>
                <h1>No results</h1>
                <p>Sorry, but we could not find any matching results.</p>
              </>
            : null}
            {!loading && combinedLength !== 0 ?
              <>
                <RowComponent>
                  <h1>Ideas ({ideas.length})</h1>
                  {ideas
                    .filter((idea) =>
                      idea.name
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()),
                    )
                    .map((idea, index) => (
                      <RowComponent.Cell
                        index={index}
                        key={idea._id}
                        as={Link}
                        to={`/ideas/${idea._id}`}
                      >
                        <div
                          style={{
                            width: 50,
                            height: 40,
                            borderRadius: 3,
                            backgroundSize: "cover",
                            backgroundPosition: "center center",
                            backgroundImage: `url(${util.mixinCssUrlFallback(util.ideaCoverImage(idea), util.ideaCoverImage())})`,
                          }}
                        >
                          {idea.isSubmitted && (
                            <Icon
                              name="check circle"
                              color="green"
                              style={{ top: -5, right: -5 }}
                            />
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            marginLeft: 5,
                            marginRight: 5,
                            position: "relative",
                            top: -1,
                          }}
                        >
                          <RowComponent.Title
                            style={{ color: "#5d5d5d" }}
                            block
                          >
                            {idea.name}
                          </RowComponent.Title>
                          <RowComponent.StatusArea>
                            {idea.isSubmitted ?
                              <>
                                <RowComponent.StatusLabel color="green" />
                                Submitted{" "}
                                {formatDistanceToNow(
                                  new Date(idea.submittedAt),
                                  { addSuffix: true },
                                )}
                              </>
                            : <>
                                <RowComponent.StatusLabel color="blue" />
                                Updated{" "}
                                {formatDistanceToNow(new Date(idea.updatedAt), {
                                  addSuffix: true,
                                })}
                              </>
                            }
                          </RowComponent.StatusArea>
                        </div>
                      </RowComponent.Cell>
                    ))}
                </RowComponent>
                <RowComponent>
                  <h1>
                    {t("common:capitalise", { key: "generic.challenges" })} (
                    {challenges.length})
                  </h1>
                  {challenges
                    .filter((challenge) =>
                      challenge.name
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()),
                    )
                    .map((challenge, index) => (
                      <RowComponent.Cell
                        index={index}
                        key={challenge._id}
                        as={Link}
                        to={`/challenges/${challenge._id}`}
                      >
                        <div
                          style={{
                            width: 50,
                            height: 40,
                            borderRadius: 3,
                            backgroundSize: "cover",
                            backgroundPosition: "center center",
                            backgroundImage: `url(${util.mixinCssUrlFallback(util.challengeImage(challenge), util.challengeImage())})`,
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            marginLeft: 5,
                            marginRight: 5,
                            position: "relative",
                            top: -1,
                          }}
                        >
                          <RowComponent.Title
                            style={{ color: "#5d5d5d" }}
                            block
                          >
                            {challenge.name}
                          </RowComponent.Title>
                          <RowComponent.StatusArea>
                            {challenge.stage === "closed" ?
                              <>
                                <RowComponent.StatusLabel color="red" /> Closed
                              </>
                            : challenge.stage === "published" &&
                              (challenge.deadline ?
                                <>
                                  <RowComponent.StatusLabel color="blue" />{" "}
                                  Closes{" "}
                                  {formatDistanceToNow(
                                    new Date(challenge.deadline),
                                    { addSuffix: true },
                                  )}
                                </>
                              : <>
                                  <RowComponent.StatusLabel color="green" />{" "}
                                  Open
                                </>)
                            }
                          </RowComponent.StatusArea>
                        </div>
                      </RowComponent.Cell>
                    ))}
                </RowComponent>
                {(
                  util.hasPermission(
                    user,
                    "org.viewDashboard",
                    user.ownerOrganisation._id,
                  )
                ) ?
                  <RowComponent noBorder>
                    <h1>Users ({users.length})</h1>
                    {users
                      .filter((filterUser) =>
                        (filterUser.profile.fullName || "")
                          .toLowerCase()
                          .includes(searchValue.toLowerCase()),
                      )
                      .map((filterUser, index) => (
                        <RowComponent.Cell
                          index={index}
                          key={filterUser._id}
                          as={Link}
                          to={`/users/${filterUser._id}`}
                        >
                          <div
                            style={{
                              width: 50,
                              height: 40,
                              borderRadius: 3,
                              backgroundSize: "cover",
                              backgroundPosition: "center center",
                              backgroundImage: `url(${util.mixinCssUrlFallback(util.avatarUrl(filterUser), util.avatarUrl())})`,
                            }}
                          />
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              marginLeft: 5,
                              marginRight: 5,
                              position: "relative",
                              top: -1,
                            }}
                          >
                            <RowComponent.Title
                              style={{ color: "#5d5d5d" }}
                              block
                            >
                              {filterUser.profile.fullName}
                            </RowComponent.Title>
                            <RowComponent.StatusArea>
                              {(
                                differenceInMonths(
                                  new Date(),
                                  new Date(filterUser.lastSeenAt),
                                ) >= 1
                              ) ?
                                <>
                                  <RowComponent.StatusLabel color="red" />
                                  {formatDistanceToNow(
                                    new Date(filterUser.lastSeenAt),
                                    { addSuffix: true },
                                  )}
                                </>
                              : <>
                                  <RowComponent.StatusLabel color="green" />
                                  {formatDistanceToNow(
                                    new Date(filterUser.lastSeenAt),
                                    { addSuffix: true },
                                  )}
                                </>
                              }
                            </RowComponent.StatusArea>
                          </div>
                        </RowComponent.Cell>
                      ))}
                  </RowComponent>
                : null}
              </>
            : null}
          </>
        }
      />
    </div>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(Tags);
